<template>
  <BasePageContainer
    v-if="goal && activity"
    ref="container"
    class="page activity_result activity_result_add"
    :style="{'padding-top': '24px', 'padding-bottom': containerPaddingBottom}">
    <div class="page__head edit-head fixed-top p-3">
      <h1 class="edit-head__heading">
        <img
          src="@/assets/images/pen-memoicon.png"
          class="img-fluid edit-head__icon mr-2">
        <span class="edit-head__activity mr-2">
          {{ repeatYearMonth }}{{ goal.name }}
        </span>
      </h1>
    </div>
    <div class="page__form general-form pt-6">
      <validation-observer
        ref="observer"
        v-slot="observer">
        <form>
          <div class="general-form__group form-group mt-3 mt-md-4">
            <label
              class="col-form-label">活動名</label>
            <validation-provider
              v-slot="{ errors }"
              rules="required|max:40"
              name="活動名">
              <input
                v-model="activity.name"
                data-testid="activity-name-input"
                type="text"
                class="form-control">
              <p class="text-primary">
                {{ errors[0] }}
              </p>
            </validation-provider>
          </div>
          <div class="general-form__group form-group mt-3 mt-md-4">
            <label
              class="col-form-label">活動日</label>
            <div>
              <validation-provider
                name="日付"
                :rules="`required|valid_date|${
                  hasRepeatGroup
                    ? 'activity_after_date_repeat:@ノート開始日|activity_before_date_repeat:@ノート終了日'
                    : 'activity_after_date:@ノート開始日|activity_before_date:@ノート終了日'
                }`">
                <b-form-datepicker
                  v-model="activity.date"
                  placeholder="日付を選択してください"
                  class="align-items-center activity-date-picker"
                  hide-header
                  :min="goal.startDate"
                  :max="goal.endDate"
                  locale="ja"
                  :date-format-options="{
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    weekday: 'short'
                  }">
                  <template v-slot:button-content>
                    <div
                      class="material-icons mr-2 d-flex align-items-center">
                      event_available
                    </div>
                  </template>
                </b-form-datepicker>
              </validation-provider>
              <!-- validationするための要素 -->
              <validation-provider name="ノート開始日">
                <input
                  :value="goal.startDate"
                  class="d-none">
              </validation-provider>
              <validation-provider name="ノート終了日">
                <input
                  :value="goal.endDate"
                  class="d-none">
              </validation-provider>
            </div>
            <p class="text-primary">
              {{ formatErrorMessage(observer['errors']['日付']) }}
            </p>
          </div>
          <div class="general-form__group form-group mt-3 mt-md-4">
            <label
              class="col-form-label mb-2">マークする</label>
            <div class="form-check p-0">
              <validation-provider name="マークする">
                <input
                  id="marked"
                  v-model="activity.star"
                  class="form-check-input"
                  type="checkbox">
                <label
                  class="form-check-label"
                  for="marked" />
              </validation-provider>
            </div>
          </div>
          <div
            ref="activityDetail"
            class="general-form__group form-group mt-3 mt-md-4">
            <div class="d-flex align-items-center">
              <label class="col-form-label mb-0">活動記録</label>
              <NoteTemplateDropdownMenu
                v-if="noteTemplates.length > 0"
                class="ml-2"
                :note-templates="noteTemplates"
                :selected-id="selectedNoteTemplateId"
                @select="onSelectNoteTemplate" />
            </div>
            <validation-provider name="活動記録">
              <textarea
                ref="textarea"
                v-model="activity.detail"
                data-testid="activity-detail-input"
                type="text"
                class="form-control"
                placeholder="活動記録を入力してください"
                :rows="rows"
                @focus="onFocusDetail('activityDetail')" />
            </validation-provider>
          </div>
          <div
            v-if="goal.conditionSetting && goal.conditionSetting.items.length"
            class="general-form__group form-group mt-3 mt-md-4">
            <label class="col-form-label">
              コンディション
            </label>
            <div class="d-flex conditions">
              <ActivityResultConditionSelect
                v-if="'fatigueLevel' in activity.conditions"
                v-model="activity.conditions.fatigueLevel"
                :condition="conditionObject.fatigueLevel"
                class="condition" />
              <ActivityResultConditionSelect
                v-if="'satisfactionLevel' in activity.conditions"
                v-model="activity.conditions.satisfactionLevel"
                :condition="conditionObject.satisfactionLevel"
                class="condition" />
              <ActivityResultConditionSelect
                v-if="'motivationLevel' in activity.conditions"
                v-model="activity.conditions.motivationLevel"
                :condition="conditionObject.motivationLevel"
                class="condition" />
              <ActivityResultConditionSelect
                v-if="'healthStatus' in activity.conditions"
                v-model="activity.conditions.healthStatus"
                :condition="conditionObject.healthStatus"
                class="condition" />
              <ActivityResultConditionSelect2Items
                v-if="'sleepHours' in activity.conditions"
                v-model="activity.conditions.sleepHours"
                :condition="conditionObject.sleepHours"
                class="condition" />
              <ActivityResultConditionSelect2Items
                v-if="'bodyTemperature' in activity.conditions"
                v-model="activity.conditions.bodyTemperature"
                :condition="conditionObject.bodyTemperature"
                class="condition" />
              <ActivityResultConditionInput
                v-if="'bodyWeight' in activity.conditions"
                v-model="activity.conditions.bodyWeight"
                :condition="conditionObject.bodyWeight"
                class="condition" />
              <ActivityResultConditionInput
                v-if="'bodyHeight' in activity.conditions"
                v-model="activity.conditions.bodyHeight"
                :condition="conditionObject.bodyHeight"
                class="condition" />
              <ActivityResultConditionSelect
                v-if="'menstrualRecord' in activity.conditions"
                v-model="activity.conditions.menstrualRecord"
                :condition="conditionObject.menstrualRecord"
                class="condition" />
              <ActivityResultConditionSelect
                v-if="'schoolLife' in activity.conditions"
                v-model="activity.conditions.schoolLife"
                :condition="conditionObject.schoolLife"
                class="condition" />
              <ActivityResultConditionSelect2Items
                v-if="'studyHours' in activity.conditions"
                v-model="activity.conditions.studyHours"
                :condition="conditionObject.studyHours"
                class="condition" />
            </div>
            <div class="condition-error-message">
              <p>
                {{ formatErrorMessage(observer['errors']['体重']) }}
              </p>
              <p>
                {{ formatErrorMessage(observer['errors']['身長']) }}
              </p>
            </div>
          </div>
          <div
            v-if="goal.bodyPainSetting != null && goal.bodyPainSetting.isRecordBodyPain"
            class="general-form__group body-pain mt-3 mt-md-4">
            <div>
              <label class="col-form-label">
                体の痛み
              </label>
              <div>
                <button
                  type="button"
                  class="body-pain__input"
                  @click="onClickBodyPain">
                  {{ isBodyPainDetailShow ? 'あり' : 'なし' }}
                </button>
              </div>
            </div>
            <div
              v-if="isBodyPainDetailShow"
              ref="bodyPainDetail"
              class="mt-2">
              <label class="col-form-label">
                痛みの詳細
              </label>
              <validation-provider
                v-slot="v"
                rules="body_pain_required|max:500"
                name="体の痛み">
                <textarea
                  v-model="activity.bodyPain"
                  type="text"
                  class="form-control"
                  placeholder="体に痛みがあれば入力してください（例：足首を捻挫。病院でレントゲンを撮りましたが特に異常はなし。）"
                  rows="3"
                  @focus="onFocusDetail('bodyPainDetail')" />
                <p class="text-primary">
                  {{ v.errors[0] }}
                </p>
              </validation-provider>
            </div>
          </div>
          <div
            v-if="attachmentOption"
            class="general-form__group form-group mt-3 mt-md-4">
            <label
              class="col-form-label">ファイルを添付</label>
            <validation-provider
              v-slot="v"
              rules="file_size">
              <file-uploader
                v-model="activity.file"
                :filename="filename" />
              <p class="text-primary">
                {{ v.errors[0] }}
              </p>
            </validation-provider>
          </div>
          <div class="general-form__btn row mt-5">
            <div class="col-6">
              <button
                type="button"
                class="btn btn-block btn-cancel"
                @click="onClickCancel">
                キャンセル
              </button>
            </div>
            <div class="col-6">
              <button
                type="button"
                class="btn btn-block btn-primary"
                @click="customPasses(onClickSave)">
                保存
              </button>
            </div>
          </div>
          <div class="row mt-5">
            <div class="col-6">
              <button
                v-if="isEdit"
                type="button"
                class="btn d-block general-form__btn-delete mx-auto text-right d-flex align-items-center"
                data-testid="delete-button"
                @click="onClickDelete">
                <span class="material-icons general-form__btn-delete__icon mr-1">
                  delete
                </span>
                削除する
              </button>
            </div>
            <div
              v-if="!isEdit || isDraft"
              class="col-6">
              <button
                type="button"
                class="btn btn-block btn-secondary"
                @click="customPasses(onClickDraft)">
                下書き保存
              </button>
            </div>
          </div>
          <div
            v-if="isEdit"
            class="general-form__item d-flex form-info justify-content-end mt-5">
            <div class="form-info__detail text-right">
              <div class="form-info__name">
                <p><b class="mr-2">作成者</b>{{ goal.createdBy.name }}</p>
              </div>
              <div class="form-info__date">
                <p>最終更新日時: {{ activity.updatedAt | prettyDateTime }}</p>
              </div>
            </div>
          </div>
        </form>
      </validation-observer>
    </div>
  </BasePageContainer>
</template>

<script>
import FileUploader from '@/components/parts/FileUploader'
import ActivityResultConditionSelect from '@/components/parts/ActivityResultConditionSelect.vue'
import ActivityResultConditionInput from '@/components/parts/ActivityResultConditionInput.vue'
import ActivityResultConditionSelect2Items from '@/components/parts/ActivityResultConditionSelect2Items.vue'
import { isIOS } from '@/helper/device'
import { fileToBase64 } from '@/utils/file'
import validate from '@/components/mixin/validate'
import { format } from 'date-fns'
import { mapGetters, mapState } from 'vuex'
import { uniq } from 'lodash'
import NoteTemplateDropdownMenu from '@/components/view/note_templates/NoteTemplateDropdownMenu.vue'

export default {
  components: {
    FileUploader,
    ActivityResultConditionSelect,
    ActivityResultConditionInput,
    ActivityResultConditionSelect2Items,
    NoteTemplateDropdownMenu
  },
  mixins: [validate],
  props: {
    activityObject: {
      type: Object,
      default: null
    },
    goalObject: {
      type: Object,
      default: null
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    isDraft: {
      type: Boolean,
      default: false
    },
    attachmentOption: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activity: {
        name: '',
        date: '',
        detail: '',
        result_count: 1,
        star: false,
        file: null,
        conditions: {},
        bodyPain: '',
        templateColor: null
      },
      goal: null,
      noteTemplates: [],
      selectedNoteTemplateId: null,
      rows: 11,
      initialContainerHeight: null,
      isIOS,
      scrollDelay: 100,
      containerPaddingBottom: '48px',
      conditionErrors: [],
      isClickBodyPain: false,
      conditionObject: {
        fatigueLevel: {name: 'fatigueLevel', label: '疲労度', unit: '10段階', type: 'select', options: [
          {value: -1, label: '今日の疲労感を選択してください'},
          {value: 0, label: '休み'},
          {value: 1, label: '1'},
          {value: 2, label: '2'},
          {value: 3, label: '3'},
          {value: 4, label: '4'},
          {value: 5, label: '5'},
          {value: 6, label: '6'},
          {value: 7, label: '7'},
          {value: 8, label: '8'},
          {value: 9, label: '9'},
          {value: 10, label: '10'},
        ], initValue: 5},
        satisfactionLevel: {name: 'satisfactionLevel', label: '充実度', unit: '10段階', type: 'select', options: [
          {value: -1, label: '今日の充実度を選択してください'},
          {value: 0, label: '休み'},
          {value: 1, label: '1'},
          {value: 2, label: '2'},
          {value: 3, label: '3'},
          {value: 4, label: '4'},
          {value: 5, label: '5'},
          {value: 6, label: '6'},
          {value: 7, label: '7'},
          {value: 8, label: '8'},
          {value: 9, label: '9'},
          {value: 10, label: '10'},
        ], initValue: 5},
        motivationLevel: {name: 'motivationLevel', label: 'モチベ', unit: '10段階', type: 'select', options: [
          {value: -1, label: '今日のモチベーションを選択してください'},
          {value: 0, label: '休み'},
          {value: 1, label: '1'},
          {value: 2, label: '2'},
          {value: 3, label: '3'},
          {value: 4, label: '4'},
          {value: 5, label: '5'},
          {value: 6, label: '6'},
          {value: 7, label: '7'},
          {value: 8, label: '8'},
          {value: 9, label: '9'},
          {value: 10, label: '10'},
        ], initValue: 5},
        healthStatus: {name: 'healthStatus', label: '体調', unit: '10段階', type: 'select', options: [
          {value: -1, label: '今日の体調を選択してください'},
          {value: 1, label: '1'},
          {value: 2, label: '2'},
          {value: 3, label: '3'},
          {value: 4, label: '4'},
          {value: 5, label: '5'},
          {value: 6, label: '6'},
          {value: 7, label: '7'},
          {value: 8, label: '8'},
          {value: 9, label: '9'},
          {value: 10, label: '10'},
        ], initValue: 5},
        sleepHours: {name: 'sleepHours', label: '睡眠', unit: '時間', type: 'select2', options1: [
          {value: '00', label: '0時間'},
          {value: '01', label: '1時間'},
          {value: '02', label: '2時間'},
          {value: '03', label: '3時間'},
          {value: '04', label: '4時間'},
          {value: '05', label: '5時間'},
          {value: '06', label: '6時間'},
          {value: '07', label: '7時間'},
          {value: '08', label: '8時間'},
          {value: '09', label: '9時間'},
          {value: '10', label: '10時間'},
          {value: '11', label: '11時間'},
          {value: '12', label: '12時間'},
        ], options2: [
          {value: '00', label: '0分'},
          {value: '05', label: '5分'},
          {value: '10', label: '10分'},
          {value: '15', label: '15分'},
          {value: '20', label: '20分'},
          {value: '25', label: '25分'},
          {value: '30', label: '30分'},
          {value: '35', label: '35分'},
          {value: '40', label: '40分'},
          {value: '45', label: '45分'},
          {value: '50', label: '50分'},
          {value: '55', label: '55分'},
        ], initValue: '07:00', splitMarker: ':'},
        studyHours: {name: 'studyHours', label: '学習', unit: '時間', type: 'select2', options1: [
          {value: '00', label: '0時間'},
          {value: '01', label: '1時間'},
          {value: '02', label: '2時間'},
          {value: '03', label: '3時間'},
          {value: '04', label: '4時間'},
          {value: '05', label: '5時間'},
          {value: '06', label: '6時間'},
          {value: '07', label: '7時間'},
          {value: '08', label: '8時間'},
          {value: '09', label: '9時間'},
          {value: '10', label: '10時間'},
          {value: '11', label: '11時間'},
          {value: '12', label: '12時間'},
        ], options2: [
          {value: '00', label: '0分'},
          {value: '05', label: '5分'},
          {value: '10', label: '10分'},
          {value: '15', label: '15分'},
          {value: '20', label: '20分'},
          {value: '25', label: '25分'},
          {value: '30', label: '30分'},
          {value: '35', label: '35分'},
          {value: '40', label: '40分'},
          {value: '45', label: '45分'},
          {value: '50', label: '50分'},
          {value: '55', label: '55分'},
        ], initValue: '00:00', splitMarker: ':'},
        bodyTemperature: {name: 'bodyTemperature', label: '体温', unit: '度', type: 'select2', options1: [
          {value: '35', label: '35.'},
          {value: '36', label: '36.'},
          {value: '37', label: '37.'},
          {value: '38', label: '38.'},
          {value: '39', label: '39.'},
          {value: '40', label: '40.'},
        ], options2: [
          {value: '0', label: '0'},
          {value: '1', label: '1'},
          {value: '2', label: '2'},
          {value: '3', label: '3'},
          {value: '4', label: '4'},
          {value: '5', label: '5'},
          {value: '6', label: '6'},
          {value: '7', label: '7'},
          {value: '8', label: '8'},
          {value: '9', label: '9'},
        ], initValue: '36.5', splitMarker: '.'},
        bodyWeight: {name: 'bodyWeight', label: '体重', unit: 'kg', type: 'input', rule: 'min_val:10|max_val:150', pattern: '^-?\\d{0,3}(?:\\.\\d{0,1})?$', initValue: null},
        bodyHeight: {name: 'bodyHeight', label: '身長', unit: 'cm', type: 'input', rule: 'min_val:80|max_val:200', pattern: '^-?\\d{0,3}(?:\\.\\d{0,1})?$', initValue: null},
        menstrualRecord: {name: 'menstrualRecord', label: '生理', unit: '○日目', type: 'select', options: [
          {value: -1, label: '生理記録を選択してください'},
          {value: 0, label: 'なし'},
          {value: 1, label: '1日目'},
          {value: 2, label: '2日目'},
          {value: 3, label: '3日目'},
          {value: 4, label: '4日目'},
          {value: 5, label: '5日目'},
          {value: 6, label: '6日目'},
          {value: 7, label: '7日目'},
          {value: 8, label: '8日目'},
          {value: 9, label: '9日目'},
          {value: 10, label: '10日目'},
        ], initValue: 0},
        schoolLife: {name: 'schoolLife', label: '学校生活', unit: '10段階', type: 'select', options: [
          {value: -1, label: '今日の学校生活の充実度を選択してください'},
          {value: 0, label: '休み'},
          {value: 1, label: '1'},
          {value: 2, label: '2'},
          {value: 3, label: '3'},
          {value: 4, label: '4'},
          {value: 5, label: '5'},
          {value: 6, label: '6'},
          {value: 7, label: '7'},
          {value: 8, label: '8'},
          {value: 9, label: '9'},
          {value: 10, label: '10'},
        ], initValue: 5},
      }
    }
  },
  computed: {
    memberId() {
      return this.$route.params.id
    },
    goalId() {
      return this.$route.params.goalId
    },
    workAt() {
      return `${this.activity.date}T00:00+09:00`
    },
    filename() {
      // 新しくファイルを添付している場合はそのファイル名を返す
      if (this.activity.file instanceof File) {
        return this.activity.file.name
      }
      return this.activity.filename
    },
    isBodyPainDetailShow() {
      if (this.activity.bodyPain === '') return this.isClickBodyPain
      return true
    },
    hasRepeatGroup() {
      return this.goal.repeatGroup !== null
    },
    repeatYearMonth() {
      if (!this.hasRepeatGroup) {
        return ''
      }
      return format(new Date(this.goal.startDate), 'M月 ')
    },
    ...mapState('auth', ['user']),
    ...mapGetters('auth', ['isAnyManager', 'accessibleDepartments']),
    // 閲覧可能なグループに所属グループが含まれていない場合があり、
    // そのままでは所属グループ限定のテンプレートが取得できないため必ず足す
    noteTemplateDepartmentIds() {
      const accessibleIds = this.accessibleDepartments.map(department => department.id)
      const ownId = this.user?.department?.id
      return uniq(ownId ? [ownId, ...accessibleIds] : accessibleIds)
    },
  },
  mounted() {
    this.goal = Object.assign({}, this.goalObject)
    // 既存ノートに templateColor が無い場合もキーを持たせて反応性を確保する
    this.activity = Object.assign({ templateColor: null }, this.activityObject)
    this.fetchNoteTemplates()

    if (this.goal.conditionSetting !== null) {
      const conditions = this.goal.conditionSetting.items.reduce((acc, key) => {
        acc[key] = this.activityObject.conditions[key]
        return acc
      }, {})
      this.activity.conditions = conditions
    }

    this.$nextTick(() => {
      this.initialContainerHeight = this.$refs.container.$el.clientHeight
    })
  },
  methods: {
    async onFocusDetail(refName) {
      // 画面上にくるようにするためには十分にスクロールできるだけの余裕が必要なので、
      // focusあたったらpadding-bottomを大きくする
      this.containerPaddingBottom = `${this.initialContainerHeight * 0.5}px`
    
      // containerの幅の更新が反映されてからスクロール
      await this.$nextTick(() => {
        this.scrollToDetail(refName)
      })
      // スクロール後は元に戻す
      this.containerPaddingBottom = '48px'
    },
    scrollToDetail(refName) {
      const target = this.$refs[refName]
      const clientRect = target.getBoundingClientRect() 
      // スクロールでヘッダーに埋もれないために、ヘッダーの高さ分差し引き
      const factor = -60
      const y = clientRect.top + window.pageYOffset + factor
      // ios safariではfocus時に自動でスクロールがかかる模様。
      // そのためsetTimeoutでスクロールのバッティングを防ぐ
      // ref: https://stackoverflow.com/questions/52652469/prevent-safari-from-automatically-scrolling-to-focused-element-scrollback-techn
      window.setTimeout(() => {
        window.scrollTo({
          top: y,
          left: 0,
          behavior: 'smooth'
        })
      }, this.scrollDelay)
    },
    async onClickSave() {
      // 活動記録の詳細が未入力の場合に確認ダイアログを出す
      if (this.activity.detail === '' && !(this.isEdit && this.activityObject.detail !== '')) {
        if (!window.confirm('活動記録の入力がありません。コンディションのみを保存する場合は通知されません。保存しますか？')) {
          return
        }
      }
      this.$emit('onClickSave', await this.formData(false))
    },
    async onClickDraft() {
      this.$emit('onClickDraft', await this.formData(true))
    },
    async formData(isDraft) {
      const form = {
        name: this.activity.name,
        workAt: this.workAt,
        detail: this.activity.detail,
        result_count: this.activity.result_count,
        star: this.activity.star,
        templateColor: this.activity.templateColor ?? null,
      }
      if (!isDraft) {
        form.goal = this.goalId
      }
      if (this.activity.file === null) {
        form.file = null
        form.filename = null
      } else if (this.activity.file instanceof File) {
        form.file = await fileToBase64(this.activity.file)
        form.filename = this.filename
      }
      if (this.goal.bodyPainSetting?.isRecordBodyPain) {
        form.body_pain = {text: this.activity.bodyPain}
      }
      if (this.goal.conditionSetting !== null) {
        form.condition = {}
        this.goal.conditionSetting.items.forEach(key => {
          const snake_key = key.split(/(?=[A-Z])/).join('_').toLowerCase()
          if (Array.isArray(this.activity.conditions[key])) {
            form.condition[snake_key] = this.activity.conditions[key].join('')
          } else {
            form.condition[snake_key] = this.activity.conditions[key]
          }
        })
      }
      return form
    },
    fetchNoteTemplates() {
      // メンバーは所属グループが参照できるものが返るためパラメータ不要
      // 管理者・限定指導者はパラメータ無しだとテナント全件が返るため、閲覧可能なグループを指定する
      // ページングで取りこぼさないよう全件を一度に取得する
      const params = this.isAnyManager
        ? { limit: 99999, department__in: this.noteTemplateDepartmentIds.join(',') }
        : { limit: 99999 }
      return this.$axios.get('/note_templates/', { params })
        .then(res => {
          this.noteTemplates = res.data.results
        })
        .catch(e => {
          // テンプレートが取得できなくてもノートの入力は継続できるため、エラーは通知しない
          console.error(e)
        })
    },
    onSelectNoteTemplate(noteTemplate) {
      // ノート活動名は任意項目のため、空のテンプレートでは入力済みの活動名を維持する
      const overwritesName = !!noteTemplate.activityName
      const hasInput = this.activity.detail !== '' || overwritesName && this.activity.name !== ''
      if (hasInput && !window.confirm('テンプレートを選択すると現在記録中の内容が消えます。よろしいですか？')) return

      if (overwritesName) {
        this.activity.name = noteTemplate.activityName
      }
      this.activity.detail = noteTemplate.detail
      this.activity.templateColor = noteTemplate.color
      this.selectedNoteTemplateId = noteTemplate.id
    },
    onClickCancel() {
      this.$router.push(`/members/${this.memberId}`)
    },
    onClickDelete() {
      this.$emit('onClickDelete')
    },
    formatErrorMessage(v) {
      if (!v) return ''
      return v[0]
    },
    onClickBodyPain() {
      if (this.activity.bodyPain !== '') return
      this.isClickBodyPain = !this.isClickBodyPain
    },
    onInputCondition(value, name) {
      this.activity.conditions[name] = value
    },
    onClickConditionButton(value, name) {
      this.activity.conditions[name] = value
    },
  }
}
</script>

<style lang="scss" scoped>

.edit-head{
  background-color: $l-blue;
  @media screen and (min-width:992px) {
    width: calc(100% - 422px);
    right: 0;
    left: auto;
  }
  &__icon{
    height: 22px;
    width: auto;
    @media screen and (max-width:320px) {
      height: 18px;
    }
  }
  &__heading{
    color: $white;
    font-size: $fs-lg;
    font-weight: bold;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }
  &__activity{
    position: relative;
    font-size: $fs-nm;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }
}

.activity-date-picker {
  max-width: 220px;
  padding-right: 16px;
}

.form-check {
  position:relative;
}

input[type="checkbox"] {
  display:none;
}

.form-check-label {
  display: block;
  // padding: 10px 10px 10px 40px;
  cursor:pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: $white;
}

.form-check-label:after {
  content: "";
  width: 20px;
  height: 20px;
  background-position: center;
  background-repeat: no-repeat;
  position: absolute;
  top: 50%;
  margin: -10px 8px;
  background-color: $grey16;
  border-radius: 50%;
}

input[type="checkbox"]:checked + .form-check-label:after {
  background-color: $pink;
}

.form-group {
  .conditions {
    gap: 10px 10px;
    flex-wrap: wrap;
  }
  .condition {
    width: calc(100% / 4 - 10px * (3 / 4));

    &__label {
      margin-bottom: 5px;
    }

    &__input {
      background-color: $white;
      color: $l-blue;
      height: 50px;
      display: flex;
      align-items: center;
      justify-content: center;

      .input-item {
        background: none;
        color: $l-blue;
        border: none;
        width: 100%;
        height: 100%;
        text-align: center;
        text-align-last: center;
      }

      .select-2-item {
        width: 100%;
        height: 100%;
        text-align: center;
        display: flex;
        justify-content: center;

        select {
          background: none;
          color: $l-blue;
          border: none;
          height: 100%;
          text-align-last: center;
          padding: 0;
          appearance: none;
        }
      }
    }
  }
  .condition-error-message {
    margin-top: 10px;
    color: $primary;

    p {
      line-height: 150%;
    }
  }
}

.body-pain__input {
  width: calc(100% / 4 - 10px * (3 / 4));
  background-color: $white;
  color: $l-blue;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}
</style>